import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FieldMapping, ScopeOption } from "../fluss-builder-types";

const VARIABLE_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

export interface ServiceOutputMapperProps {
    value: FieldMapping[];
    outputs: string[];
    variables: ScopeOption[];
    disabled?: boolean;
    allowCustomOutputs?: boolean;
    onChange: (value: FieldMapping[]) => void;
}

const ServiceOutputMapper = ({
    value,
    outputs,
    variables,
    disabled = false,
    allowCustomOutputs = false,
    onChange,
}: ServiceOutputMapperProps) => {
    const { t } = useTranslation();
    const updateRow = (index: number, patch: Partial<FieldMapping>) => {
        onChange(value.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    };
    const removeRow = (index: number) => onChange(value.filter((_, rowIndex) => rowIndex !== index));
    const targetMessage = (target: string, index: number) => {
        if (!target) return "";
        if (!VARIABLE_NAME_PATTERN.test(target)) {
            return t("fluss.node_panel.service.output_variables.invalid", {
                defaultValue: "Use letters, numbers, _, . or -, starting with a letter or _.",
            });
        }
        if (value.some((row, rowIndex) => rowIndex !== index && row.target === target)) {
            return t("fluss.node_panel.service.output_variables.duplicate", {
                defaultValue: "Variable name already mapped.",
            });
        }
        return "";
    };
    const variableOptions = variables.map((variable) => variable.name);

    return (
        <Stack spacing={1} data-testid="service-output-mapper" className="fluss-mapper">
            <Box
                sx={{ display: "grid", gap: 1 }}
            >

                {value.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ gridColumn: "1 / -1", py: 1 }}>
                        {t("fluss.node_panel.service.output_variables.empty", {
                            defaultValue: "Add an output to expose it as a process variable.",
                        })}
                    </Typography>
                )}

                {value.map((mapping, index) => {
                    const source = typeof mapping.source === "string" ? mapping.source.replace(/^output\./, "") : "";
                    const message = targetMessage(mapping.target, index);
                    return (
                        <Box key={index} className="fluss-mapper__row" data-testid="service-output-mapper-row">
                            <Box className="fluss-mapper__destination">
                                <Typography variant="caption" color="text.secondary">
                                    {t("fluss.node_panel.service.output_variables.source", { defaultValue: "Output" })}
                                </Typography>
                            {allowCustomOutputs ? (
                                <Autocomplete
                                    size="small"
                                    freeSolo
                                    fullWidth
                                    disabled={disabled}
                                    options={outputs}
                                    value={source}
                                    onInputChange={(_, next) => updateRow(index, { source: next ? `output.${next}` : undefined })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputProps={{
                                                ...params.inputProps,
                                                "aria-label": String(t("fluss.node_panel.service.output_variables.source", { defaultValue: "Output" })),
                                            }}
                                        />
                                    )}
                                />
                            ) : (
                                <Select
                                    size="small"
                                    value={source}
                                    disabled={disabled}
                                    displayEmpty
                                    inputProps={{
                                        "aria-label": String(t("fluss.node_panel.service.output_variables.source", { defaultValue: "Output" })),
                                    }}
                                    onChange={(event) => updateRow(index, { source: `output.${event.target.value}` })}
                                >
                                    <MenuItem value="" disabled>
                                        {t("fluss.node_panel.service.output_variables.select_output", { defaultValue: "Select output" })}
                                    </MenuItem>
                                    {outputs.map((output) => (
                                        <MenuItem key={output} value={output}>{output}</MenuItem>
                                    ))}
                                </Select>
                            )}
                            </Box>
                            <Typography className="fluss-mapper__arrow" aria-hidden="true" color="text.secondary" textAlign="center" fontWeight={700}>←</Typography>
                            <Box className="fluss-mapper__source">
                                <Typography variant="caption" color="text.secondary">
                                    {t("fluss.node_panel.service.output_variables.target", { defaultValue: "Variable" })}
                                </Typography>
                            <Autocomplete
                                size="small"
                                freeSolo
                                fullWidth
                                disabled={disabled}
                                options={variableOptions}
                                value={mapping.target || ""}
                                onInputChange={(_, next) => updateRow(index, { target: next })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(message)}
                                        helperText={message}
                                        inputProps={{
                                            ...params.inputProps,
                                            "aria-label": String(t("fluss.node_panel.service.output_variables.target", { defaultValue: "Variable" })),
                                        }}
                                    />
                                )}
                            />
                            </Box>
                            <Tooltip title={t("fluss.node_panel.service.output_variables.remove", { defaultValue: "Remove output" })}>
                                <span className="fluss-mapper__remove">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={disabled}
                                        aria-label={String(t("fluss.node_panel.service.output_variables.remove", { defaultValue: "Remove output" }))}
                                        onClick={() => removeRow(index)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>
            <Box>
                <Button
                    size="small"
                    disabled={disabled}
                    startIcon={<AddIcon />}
                    onClick={() => onChange([...value, { target: "", source: undefined }])}
                >
                    {t("fluss.node_panel.service.output_variables.add", { defaultValue: "Output" })}
                </Button>
            </Box>
        </Stack>
    );
};

export default ServiceOutputMapper;
